import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Edit, Trash2, XCircle, Pause, Play, History, Zap } from 'lucide-react';
import { useMyRaffles, useDeleteRaffle, useCloseRaffle, usePauseRaffle } from '../../hooks/useRaffles';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Badge } from '../../components/common/Badge';
import { Spinner } from '../../components/common/Spinner';
import { Select } from '../../components/common/Select';
import { DrawPeriods } from '../../components/raffle/DrawPeriods';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { RAFFLE_STATUS_COLORS } from '../../utils/constants';
import { useAuth } from '../../context/AuthContext';
import { adminService } from '../../services/adminService';
import toast from 'react-hot-toast';

export const AdminRaffles: React.FC = () => {
  const { user } = useAuth();
  const isAuditor = user?.role === 'AUDITOR';
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const { data, isLoading } = useMyRaffles({ page, limit: 10, status });
  const deleteRaffle = useDeleteRaffle();
  const closeRaffle = useCloseRaffle();
  const pauseRaffle = usePauseRaffle();

  if (isLoading) {
    return <AdminLayout><div className="flex justify-center py-12"><Spinner size="lg" /></div></AdminLayout>;
  }

  const raffles = data?.data?.raffles || [];
  const pagination = data?.data?.pagination;

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this raffle?')) {
      await deleteRaffle.mutateAsync(id);
    }
  };

  const handleClose = async (id: string) => {
    if (window.confirm('Are you sure you want to close this raffle?')) {
      await closeRaffle.mutateAsync(id);
    }
  };

  const handlePause = async (id: string) => {
    await pauseRaffle.mutateAsync(id);
  };

  const handleTriggerDraw = async (raffleId: string) => {
    if (window.confirm('Manually trigger a draw for this raffle?')) {
      try {
        await adminService.triggerDraw(raffleId);
        toast.success('Draw triggered successfully');
      } catch {
        toast.error('Failed to trigger draw');
      }
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Raffles</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Create and manage platform raffles</p>
        </div>
        {!isAuditor && (
          <Link to="/admin/raffles/create">
            <Button><Plus className="h-4 w-4 mr-2" />Create Raffle</Button>
          </Link>
        )}
      </div>

      <div className="mb-6 w-64">
        <Select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          options={[
            { value: '', label: 'All Status' },
            { value: 'PENDING_START', label: 'Pending Start' },
            { value: 'ACTIVE', label: 'Active' },
            { value: 'PAUSED', label: 'Paused' },
            { value: 'CLOSED', label: 'Closed' },
            { value: 'ENDED', label: 'Ended' },
          ]}
        />
      </div>

      {raffles.length === 0 ? (
        <Card><p className="text-center text-gray-400 dark:text-gray-500 py-12">No raffles found.</p></Card>
      ) : (
        <>
          <div className="space-y-4">
            {raffles.map((raffle) => (
              <Card key={raffle.id} className="hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-3">
                    <Link to={`/raffles/${raffle.id}`} className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                      <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">{raffle.name}</h3>
                    </Link>
                    <Badge variant={RAFFLE_STATUS_COLORS[raffle.status]}>{raffle.status}</Badge>
                  </div>
                  <div className="flex gap-2">
                    <Link to={`/admin/raffles/${raffle.id}/draws`}>
                      <Button variant="outline" size="sm" title="View Draw History"><History className="h-4 w-4" /></Button>
                    </Link>
                    {!isAuditor && (
                      <>
                        {(raffle.status === 'ACTIVE' || raffle.status === 'CLOSED') && (
                          <Button variant="outline" size="sm" onClick={() => handleTriggerDraw(raffle.id)} title="Trigger Draw">
                            <Zap className="h-4 w-4" />
                          </Button>
                        )}
                        {raffle.ticketsSold === 0 && (
                          <>
                            <Link to={`/admin/raffles/${raffle.id}/edit`}>
                              <Button variant="outline" size="sm"><Edit className="h-4 w-4" /></Button>
                            </Link>
                            <Button variant="danger" size="sm" onClick={() => handleDelete(raffle.id)} isLoading={deleteRaffle.isPending}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {raffle.status === 'ACTIVE' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handlePause(raffle.id)} isLoading={pauseRaffle.isPending} title="Pause Raffle">
                              <Pause className="h-4 w-4" />
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => handleClose(raffle.id)} isLoading={closeRaffle.isPending} title="Close Raffle">
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {raffle.status === 'PAUSED' && (
                          <>
                            <Button variant="outline" size="sm" onClick={() => handlePause(raffle.id)} isLoading={pauseRaffle.isPending} title="Resume Raffle">
                              <Play className="h-4 w-4" />
                            </Button>
                            <Button variant="danger" size="sm" onClick={() => handleClose(raffle.id)} isLoading={closeRaffle.isPending} title="Close Raffle">
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
                <p className="text-gray-500 dark:text-gray-400 mb-4 line-clamp-2">{raffle.description}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  {[
                    { label: 'Total Tickets Sold', value: raffle.ticketsSold },
                    { label: 'Total Sales', value: formatCurrency(raffle.totalSales, raffle.currency) },
                    { label: 'Ticket Price', value: formatCurrency(raffle.ticketPrice, raffle.currency) },
                    { label: 'Sales End', value: formatDate(raffle.salesEndDate) },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400">{stat.label}</p>
                      <p className="font-semibold text-gray-900 dark:text-gray-100 mt-0.5">{stat.value}</p>
                    </div>
                  ))}
                </div>
                <div className="pt-4 border-t border-gray-100 dark:border-gray-800">
                  <DrawPeriods raffle={raffle} variant="compact" />
                </div>
              </Card>
            ))}
          </div>
          {pagination && pagination.pages > 1 && (
            <div className="flex justify-center items-center gap-4 mt-8">
              <Button variant="outline" disabled={page === 1} onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Previous</Button>
              <span className="text-sm text-gray-500 dark:text-gray-400">Page {page} of {pagination.pages}</span>
              <Button variant="outline" disabled={page === pagination.pages} onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Next</Button>
            </div>
          )}
        </>
      )}
    </AdminLayout>
  );
};
